import { useEffect, useMemo, useRef } from 'react'
import { useGameUrlMutation } from '@/features/game-api/use-game-url-mutation/use-game-url-mutation'
import { useWalletStore } from '@/features/wallet-store/use-wallet-store'
import { SystemClock } from '@/features/system-clock/system-clock'
import { createFileRoute, useLocation } from '@tanstack/react-router'
import { GamePlayRootLayout } from '../../-game-play-root-layout'
import { cn } from '@vgw/tailwind-merger'
import { useGameMessageHandler } from '@/features/gameplay/use-game-message-handler'
import { BrandedLoadingAnimation } from '@/features/gameplay/branded-loading-animation/branded-loading-animation'
import { GamePlayError } from '@/features/game-play/game-play-error'
import { GamePlayGeoBlockError } from '@/features/game-play/game-play-geo-block-error'
import { useFeatureFlag } from '@/lib/use-feature-flag'
import { assertCanPlayBeforeLoad } from '@/features/ctrls-and-caps/assert-can-play-before-load'
import { useGameAccessAttemptedTracking } from '@/features/game-tracking/use-game-access-attempted-tracking'
import { useBrandGamesQuery } from '@/features/games-api/use-brand-games-query/use-brand-games-query'
import { useGamePageTracking } from '@/features/game-play/use-game-page-tracking'

export const Route = createFileRoute('/_app/game-play/$gameId')({
  component: GamePlay,
  beforeLoad: async () => {
    return assertCanPlayBeforeLoad()
  },
})

function GamePlay() {
  const { data, isLoading: isFeatureFlagsLoading } = useFeatureFlag({
    flagKey: 'eclipse-release-game-play-branded-animation',
  })

  const isBrandedAnimationEnabled = data?.enabled

  const { gameId } = Route.useParams()
  const { state } = useLocation()

  const coinType = useWalletStore((state) => state.coinType)

  const { data: games, error: brandGamesError } = useBrandGamesQuery()

  const {
    getGameUrlMutation,
    data: launchedGame,
    error: launchedGameError,
  } = useGameUrlMutation()

  const game = useMemo(
    () => games?.content.find((game) => game.gameId === gameId),
    [games, gameId],
  )

  useGamePageTracking({
    gameId,
    gameName: game?.name,
  })

  const {
    isGameLoading,
    isError,
    isGeoBlockError: isGeoBlockMessageReceived,
  } = useGameMessageHandler({
    launchUrl: launchedGame?.launchUrl,
  })

  const { trackGameCardAccessed } = useGameAccessAttemptedTracking()
  const hasTrackedRef = useRef(false)

  const isGeoBlockError = useMemo(() => {
    const isGeoBlockResponse = !!launchedGameError?.details?.errors?.find(
      ({ errorName }) => errorName === 'BlockedByGeofencing',
    )

    return isGeoBlockResponse || isGeoBlockMessageReceived
  }, [launchedGameError, isGeoBlockMessageReceived])

  const shouldShowIframe = useMemo(() => {
    if (isFeatureFlagsLoading) {
      return false
    }

    if (!isBrandedAnimationEnabled) {
      return true
    }

    return !isGameLoading
  }, [isFeatureFlagsLoading, isBrandedAnimationEnabled, isGameLoading])

  const shouldShowBrandedAnimation =
    !isFeatureFlagsLoading && !!isBrandedAnimationEnabled && !!isGameLoading

  useEffect(() => {
    if (!game) {
      return
    }
    getGameUrlMutation({
      launchUrl: game.launchUrl,
      selectedCoinType: coinType,
    })
  }, [coinType, getGameUrlMutation, game])

  useEffect(() => {
    if (!state?.from && !hasTrackedRef.current && game?.name) {
      hasTrackedRef.current = true
      trackGameCardAccessed({
        gameId,
        gameName: game.name,
        gameEntryPoint: 'deeplink',
      })
    }
  }, [trackGameCardAccessed, gameId, game?.name, state])

  if (isGeoBlockError) {
    return (
      <GamePlayRootLayout>
        <main className="flex w-full flex-1 flex-col bg-page-small-background bg-contain bg-top bg-no-repeat px-8 py-10 2xl:bg-cover">
          <GamePlayGeoBlockError />
        </main>
      </GamePlayRootLayout>
    )
  }

  if (brandGamesError || launchedGameError || isError) {
    return (
      <GamePlayRootLayout>
        <main
          className="flex w-full flex-1 flex-col bg-page-small-background bg-contain bg-top bg-no-repeat px-8 py-10 2xl:bg-cover"
          data-testid="game-play-error"
        >
          <GamePlayError />
        </main>
      </GamePlayRootLayout>
    )
  }

  return (
    <GamePlayRootLayout mainClassName="bg-black">
      <div className="relative flex w-full flex-1">
        {launchedGame && !launchedGame.providesClock ? (
          <SystemClock className="absolute top-0.5 left-0.5" />
        ) : null}
        {launchedGame?.launchUrl ? (
          <iframe
            title="Game"
            className={cn('flex-1 transition-opacity duration-500', {
              'opacity-0': !shouldShowIframe,
              'opacity-100': shouldShowIframe,
            })}
            src={launchedGame.launchUrl}
            data-testid="game-play-iframe"
          />
        ) : null}
        {!!shouldShowBrandedAnimation && (
          <BrandedLoadingAnimation className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        )}
      </div>
    </GamePlayRootLayout>
  )
}
